"use client";

import { useCallback, useEffect, useRef, useState } from "react";

// =============================================================================

/**
 * Controlled / uncontrolled hybrid state (Radix `value` / `defaultValue` /
 * `onChange` pattern): the setter always notifies `onChange` and only writes
 * the internal state when `value` is undefined.
 */
export function useControllableState<T>(
  value: T | undefined,
  defaultValue: T | (() => T),
  onChange?: (next: T) => void
): [T, (next: T) => void] {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;

  // Latest onChange without re-creating the setter
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const set = useCallback(
    (next: T) => {
      if (!isControlled) setInternal(() => next);
      onChangeRef.current?.(next);
    },
    [isControlled]
  );

  return [isControlled ? value : internal, set];
}
